import React, { useEffect, useRef } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';

import { AlertList, Button, ErrorResponse, Spinner } from '~components';
import Icon, { faFileCsv } from '~components/icon';
import { TableView } from '~components/table-view';

import buildColumns from '~support-at-home/forms/budgets/bulkCopyPreviewList';

import { BudgetPreviewRow, useBulkCopyPreviewMutation } from '~support-at-home/actions/budgets';

import CategoryMoveNotice, { groupMovesFromMovedServices } from '../CategoryMoveNotice';

import { BulkCopyCsvRow, exportBulkCopyReviewCsv } from './bulkCopyCsv';
import FailureList from './FailureList';

interface ComponentProps {
  value: {
    budget_ids: string[];
    start_date: string;
    end_date: string;
    price_plan_revision_id?: string;
    apply_category_moves?: boolean;
  };
  onChange?: (name: string, value: any) => any;
}

/** The bulk run's hint. It has per-budget counts on screen, not sections. */
const BULK_APPLIED_HINT =
  'check the counts below before confirming, or leave them out if you would rather add them yourself.';

const PreviewPanel: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { budget_ids, start_date, end_date, price_plan_revision_id, apply_category_moves } = value;
  // Undefined until the provider touches the switch. Moving the services is what the run does unless
  // they say otherwise, so only an explicit false turns it off.
  const applyCategoryMoves = apply_category_moves !== false;
  const [preview, { data, error, isLoading }] = useBulkCopyPreviewMutation();

  useEffect(() => {
    preview({ budget_ids, start_date, end_date, target_revision_id: price_plan_revision_id ?? null });
  }, []);

  // `total` counts every SELECTED budget, so the un-copyable ones have to be subtracted before it can be
  // described as "will be copied" — otherwise the headline double-counts them against the not-copied table
  // right below it ("1 budget will be copied" + "1 Budget Will Not Be Copied" for the same budget).
  // Caveat inherited from the >150 partial preview: `failed` only covers the previewed subset, so this is a
  // best-known figure, exactly like the needs_review breakdown.
  // Fall back to the row count if `failed` is missing (an older cached response), so the headline can't
  // claim a budget will be copied while the table below lists it as un-copyable.
  const failedCount = data?.failed ?? data?.failures?.length ?? 0;
  // Budgets carrying at least one category move. Counted over budgets, not services, because the
  // sentence it feeds is about budgets - and a budget with three moved lines is still one budget.
  const movedBudgetCount = (data?.budgets ?? []).filter((budget) => (budget.moved_count ?? 0) > 0).length;
  // Every moved line the preview found, collapsed into one statement of the change. Built from what
  // the preview DETECTED rather than from the choice, so it survives the switch being turned off.
  const moveGroups = groupMovesFromMovedServices(
    (data?.budgets ?? []).flatMap((budget) => budget.moved_services ?? []),
  );
  const copyableCount = Math.max((data?.total ?? 0) - failedCount, 0);

  // Gate "Confirm Copy": only enable it once the dry-run has settled successfully so the
  // coordinator can't skip past the impact preview into the real copy. The wizard's disableNext
  // reads this from Formik values. Guarded by a ref because onChange's identity churns every
  // Formik render (see ListAndConfirm), which would otherwise loop.
  const readyRef = useRef<boolean | null>(null);
  useEffect(() => {
    const ready = !isLoading && !error && !!data;
    if (readyRef.current === ready) return;
    readyRef.current = ready;
    onChange?.('preview_ready', ready);
  }, [isLoading, error, data, onChange]);

  // Also gate it on there being something left to copy: if every selected budget is un-copyable, confirming
  // would kick off a run that creates nothing. Published separately from preview_ready so the two reasons
  // for a disabled Confirm stay distinguishable (see bulkCopy.ts step 3).
  const hasCopyableRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!data) return;
    const hasCopyable = copyableCount > 0;
    if (hasCopyableRef.current === hasCopyable) return;
    hasCopyableRef.current = hasCopyable;
    onChange?.('has_copyable_budgets', hasCopyable);
  }, [data, copyableCount, onChange]);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner loading={isLoading} />
        <p className="mt-3 text-muted">Checking affected budgets...</p>
      </div>
    );
  }

  if (error) return <ErrorResponse message={getErrorMessage(error)} />;

  if (!data) return null;

  /**
   * The services that moved category, as one readable phrase per budget, for the export.
   *
   * "Personal Care -> Clinical Supports" rather than a count. The table states the change once above
   * itself and carries only a count, but a CSV has no banner to lean on - read on its own, a column
   * of bare numbers says a service did something without saying which service or what.
   */
  const movedServicesLabel = (row: BudgetPreviewRow): string =>
    (row.moved_services ?? [])
      .map((moved) => `${moved.service_type_name ?? 'Service'} \u2192 ${moved.to_category_name ?? 'new category'}`)
      .join('; ');

  /**
   * Services that will not come across on this budget.
   *
   * The preview is always reconciled as though the moves are being carried across - that is what
   * keeps the change detectable after a provider switches it off, and a detection that disappeared
   * with the choice would take the switch and the column with it. So the server's removed_count is
   * the figure for carrying them; declining puts the moved services back among the removals, and the
   * count has to say so rather than quietly under-reporting what the run is about to leave behind.
   */
  const removedCount = (row: BudgetPreviewRow): number =>
    applyCategoryMoves ? row.removed_count : row.removed_count + (row.moved_count ?? 0);

  /**
   * Services actually being moved on this budget, which is none once the move is declined.
   *
   * The two columns have to add up to the number of services affected. Leaving this at the detected
   * figure while the removals absorbed the same services counted them twice - a budget with one
   * personal care line and one genuine removal read as "2 removed, 1 moved", which is three services
   * on a budget that has two. The COLUMN still appears whenever a move was detected; it is the
   * figure in it that follows the choice.
   */
  const movedCount = (row: BudgetPreviewRow): number => (applyCategoryMoves ? (row.moved_count ?? 0) : 0);

  const toCsvRows = (budgets: BudgetPreviewRow[]): BulkCopyCsvRow[] =>
    budgets.map((row) => ({
      client_name: row.client_name,
      budget_id: row.budget_id,
      budget_reference: row.ref,
      start_date,
      end_date,
      services_removed: removedCount(row),
      services_moved: applyCategoryMoves ? movedServicesLabel(row) : '',
      services_price_missing: row.price_missing_count,
      needs_review: row.needs_review,
    }));

  // The CSV always lists EVERY selected budget (AC: `ok` for clean ones), so it stays available
  // even in the all-clean case below — the coordinator can still keep a written record. The impact
  // table further down only shows the needs_review rows. Guarded so an empty result set (no budgets)
  // never offers an empty download.
  const downloadButton = data.budgets.length > 0 && (
    <Button
      color="info"
      onClick={() => exportBulkCopyReviewCsv(toCsvRows(data.budgets), 'bulk-copy-preview-issues')}
      dataCy="bulk-copy-preview-export-button"
    >
      <Icon icon={faFileCsv} className="me-2" />
      Download impact list
    </Button>
  );

  // Requests over 150 ids still succeed, but the server only builds detail rows for the first
  // `previewed` budgets — the clean/needs_review breakdown below is scoped to that subset only.
  const isPartialPreview = data.total > data.previewed;

  // Budgets the copy would reject outright (e.g. no resolvable funding source). Listed here so they're known
  // BEFORE the copy runs; budgets beyond the previewed subset can still surface at step 4 (TAV-2649a).
  const failures = data.failures ?? [];
  const failureList = (
    <FailureList failures={failures} headingSuffix="Will Not Be Copied" idPrefix="bulk-copy-preview" />
  );

  // Headline count. With un-copyable budgets present it has to name both figures, or it reads as though the
  // budget in the not-copied table below is also going to be copied.
  const headline =
    failedCount > 0 ? (
      <>
        <strong>{copyableCount}</strong> of {data.total} {data.total === 1 ? 'budget' : 'budgets'} will be
        copied - {failedCount} cannot be copied.
      </>
    ) : (
      <>
        <strong>{data.total}</strong> {data.total === 1 ? 'budget' : 'budgets'} will be copied.
      </>
    );

  if (data.needs_review === 0) {
    return (
      <div>
        <p className="mb-2">{headline}</p>
        {/* Nothing left to copy means there is nothing for the price plan to affect either — the
            reassuring "no budgets will be affected" alert would just contradict the failure table. */}
        {copyableCount > 0 && (
          <AlertList
            color="info"
            header={
              isPartialPreview
                ? `No budgets will be affected by this price plan, showing impact for the first ${data.previewed} of ${data.total} budgets.`
                : 'No budgets will be affected by this price plan.'
            }
            listItems={[]}
          />
        )}
        {downloadButton && <div className="d-flex justify-content-end">{downloadButton}</div>}
        {failureList}
      </div>
    );
  }

  const rows = data.budgets
    .filter((budget) => budget.needs_review)
    .map((budget) => ({
      ...budget,
      id: budget.budget_id,
      removed_count: removedCount(budget),
      moved_count: movedCount(budget),
    }));

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <p className="mb-0">
          {headline}{' '}
          {isPartialPreview
            ? `Showing impact for the first ${data.previewed} of ${data.total} budgets: ` +
              `${data.needs_review} need review after copy.`
            : // Denominator is the copyable count, not the selection: a budget that isn't being copied
              // can't "have services removed after copy".
              `${data.needs_review} of ${copyableCount} budgets need review after copy.`}
        </p>
        {downloadButton}
      </div>
      <CategoryMoveNotice
        groups={moveGroups}
        applyCategoryMoves={applyCategoryMoves}
        onToggle={(next) => onChange?.('apply_category_moves', next)}
        scopeLabel={movedBudgetCount === 1 ? 'the budget being copied' : 'the budgets being copied'}
        appliedHint={BULK_APPLIED_HINT}
        dataCy="bulk-copy-category-moves"
      />
      {/* Height-capped + scrollable: see .bulk-copy-scroll-table in _claims.scss */}
      <div className="bulk-copy-scroll-table">
        <TableView
          id="bulk-copy-preview-table"
          data={rows}
          columns={buildColumns(moveGroups.length > 0)}
          readonly
          striped
        />
      </div>
      {failureList}
    </div>
  );
};

export default PreviewPanel;
